








































































































import Vue from 'vue';
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import { ISegment, IABTest } from '@/store/types';
import * as actions from '@/store/action-types';
import * as mutations from '@/store/mutation-types';
import CancelFlowCard from '@/components/Builder/CancelFlowCard.vue';
import ABTestInfo from '@/components/Cancellation/ABTests/ABTestInfo.vue';
import ABTestCreateModal from '@/components/Cancellation/ABTests/ABTestCreateModal.vue';
import { descending } from 'd3-array';

interface ISegmentAndBlueprint {
  segmentId: string;
  blueprintId?: string;
}

export default Vue.extend({
  name: 'CancellationFlows',
  components: {
    draggable,
    CancelFlowCard,
    ABTestInfo,
    ABTestCreateModal,
  },
  data() {
    return {
      drag: false as boolean,
      order: [] as string[],
      publishLoading: {} as { [key: string]: boolean },
      abTest: {
        name: '',
        winner: '',
        segmentId: '',
      } as {
        [key: string]: string | IABTest | undefined;
        name: string;
        winner: string;
        segmentId: string;
        selectedABTest: IABTest | undefined;
      },
      showABTestConfirm: false as boolean,
    };
  },
  computed: {
    ...mapGetters(['primaryBlueprintId', 'abTests']),
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
      };
    },
    segmentMap(): { [key: string]: ISegment } {
      return this.$store.state.segments;
    },
    segmentIds(): string[] {
      if (this.$store.state.segments) {
        return this.$store.state.org.segments.filter((id: string) => this.segmentMap[id] && !this.segmentMap[id].deleted);
      }
      return [];
    },
    // object with segment and associated blueprint id
    segmentsWithBlueprints(): ISegmentAndBlueprint[] {
      return this.segmentIds.map((segmentId) => {
        let blueprintId;
        const segment = this.segmentMap[segmentId];
        if (segment) {
          if (segment.blueprints && segment.blueprints.length) {
            blueprintId = segment.blueprints[0];
          }
        }
        return {
          segmentId,
          blueprintId,
        };
      });
    },
    enabledList(): ISegmentAndBlueprint[] {
      return this.segmentsWithBlueprints
        .filter(({ segmentId }) => {
          const segment = this.segmentMap[segmentId];
          return segment && segment.enabled;
        })
        .sort((a, b) => {
          const aPublished = this.segmentMap[a.segmentId].activeBlueprint ? 1 : 0;
          const bPublished = this.segmentMap[b.segmentId].activeBlueprint ? 1 : 0;
          return descending(aPublished, bPublished);
        });
    },
    disabledList(): ISegmentAndBlueprint[] {
      return this.segmentsWithBlueprints.filter(({ segmentId }) => {
        const segment = this.segmentMap[segmentId];
        return segment && !segment.enabled;
      });
    },
    groupedEnabledSegments(): { abtest: { _id: string }; segments: { segmentId: string; blueprintId: string | undefined }[] }[] {
      const mappedSegment: {
        abtest: { _id: string };
        segments: { segmentId: string; blueprintId: string | undefined }[];
      }[] = [];
      let foundABTest = null as any;
      if (this.abTests !== undefined) {
        this.enabledList.forEach(({ segmentId, blueprintId }) => {
          let hasABTest = false as boolean;
          mappedSegment.forEach((mapSegment) => {
            if (mapSegment.segments) {
              mapSegment.segments.forEach((item) => {
                if (item.segmentId === segmentId) {
                  hasABTest = true;
                  item.blueprintId = blueprintId;
                }
              });
            }
          });
          this.abTests.forEach((item: IABTest) => {
            if (!item.completedAt && item.segments.includes(segmentId) && item.segments[1] === segmentId) {
              foundABTest = item;
            } else if (!item.completedAt && item.segments.includes(segmentId)) {
              foundABTest = {
                ...item,
                isFirst: true,
              };
            }
          });
          if (foundABTest === null) {
            mappedSegment.push({
              abtest: {
                _id: (Math.random() + 1).toString(36).substring(7),
              },
              segments: [{ segmentId, blueprintId }],
            });
          } else if (foundABTest && !hasABTest) {
            const withBlueprint = foundABTest.segments.map((item: string) => {
              return {
                segmentId: item,
                blueprintId: segmentId === item ? blueprintId : undefined,
              };
            });
            mappedSegment.push({
              abtest: foundABTest,
              segments: withBlueprint,
            });
          }
          foundABTest = null;
        });
      }
      return mappedSegment;
    },
  },
  methods: {
    // @ts-ignore
    updateABTest(segment, payload) {
      this.drag = true;
      segment.abtest = {
        ...segment.abtest,
        ...payload,
      };
      this.drag = false;
    },
    // @ts-ignore
    onDragEnd(order) {
      if (order.newIndex !== order.oldIndex) {
        const listState = [] as string[];
        this.groupedEnabledSegments.forEach((groupEnabled) => {
          const segments = groupEnabled.segments.map((segment) => {
            return segment.segmentId;
          });
          listState.push(...segments);
        });
        this.$store.dispatch(actions.UPDATE_SEGMENT_ORDER, { segments: listState });
      }
      this.drag = false;
    },
    handlePreview(blueprintId: string) {
      if (blueprintId) {
        this.$store.commit(mutations.SET_PREVIEW_INLINE, false);
        this.$store.dispatch(actions.LAUNCH_EMBED_PREVIEW, { blueprintId });
      }
    },
    handleEdit(blueprintId: string) {
      if (blueprintId) {
        this.$router.push({ name: 'builder', params: { id: blueprintId } });
      }
    },
    handleDelete(segmentId: string) {
      if (segmentId) {
        this.$store.commit(mutations.STORE_SEGMENT_DATA, { id: segmentId, data: { deleted: true } });
        this.$store.dispatch(actions.SEGMENT_SCHEDULE_SAVE, { id: segmentId, delay: 0 });
      }
    },
    async handlePublish(blueprintId: string, segmentId: string) {
      try {
        if (blueprintId && segmentId) {
          this.publishLoading[segmentId] = true;
          await this.$store.dispatch(actions.BLUEPRINT_SEGMENT_PUBLISH, { id: blueprintId, segmentId });
          this.publishLoading[segmentId] = false;
          this.$message.success("It's live! Segment successfully published.");
        }
      } catch (e) {
        this.$message.error('Failed to publish segment. Please refresh and try again or contact us through chat');
        this.publishLoading[segmentId] = false;
        console.log(e);
      }
    },
    handleDisable(segmentId: string) {
      if (segmentId) {
        this.$store.commit(mutations.STORE_SEGMENT_DATA, { id: segmentId, data: { enabled: false } });
        this.$store.dispatch(actions.SEGMENT_SCHEDULE_SAVE, { id: segmentId, delay: 0 });
      }
    },
    handleEnable(segmentId: string) {
      if (segmentId) {
        this.$store.commit(mutations.STORE_SEGMENT_DATA, { id: segmentId, data: { enabled: true } });
        this.$store.dispatch(actions.SEGMENT_SCHEDULE_SAVE, { id: segmentId, delay: 0 });
      }
    },
    handleClone(segmentId: string) {
      if (segmentId) {
        this.$store.dispatch(actions.SEGMENT_CLONE, { id: segmentId, delay: 0 });
      }
    },
    handleABTest(segmentId: string) {
      if (segmentId) {
        this.abTest.name = '';
        this.abTest.segmentId = segmentId;
        this.showABTestConfirm = true;
      }
    },
    closeCreateABTestDialog() {
      this.abTest.name = '';
      this.abTest.segmentId = '';
      this.showABTestConfirm = false;
    },
  },
});
